import { useEffect, useRef } from "react";

/**
 * AnimatedGrid — modern SVG grid with subtle scan animation.
 * Adds a tech/fintech feel to light sections. Very low opacity so it
 * never fights with the content. Respects prefers-reduced-motion.
 */
export default function AnimatedGrid({
  variant = "light",
  opacity = 0.42,
  cellSize = 56,
  className = "",
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const el = ref.current;
    if (!el) return;
    let raf;
    let t0 = performance.now();
    const tick = (t) => {
      const dt = (t - t0) / 1000;
      // Slow diagonal drift — 22 sec period
      const x = ((dt / 22) % 1) * cellSize;
      const y = ((dt / 34) % 1) * cellSize;
      el.style.backgroundPosition = `${x.toFixed(2)}px ${y.toFixed(2)}px`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [cellSize]);

  const stroke = variant === "dark" ? "rgba(255,255,255,0.06)" : "rgba(10,10,10,0.055)";

  return (
    <div
      ref={ref}
      aria-hidden
      className={`pointer-events-none absolute inset-0 ${className}`}
      style={{
        opacity,
        backgroundImage: `linear-gradient(${stroke} 1px, transparent 1px), linear-gradient(90deg, ${stroke} 1px, transparent 1px)`,
        backgroundSize: `${cellSize}px ${cellSize}px`,
        maskImage: "radial-gradient(ellipse at center, black 40%, transparent 78%)",
        WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 78%)",
      }}
    />
  );
}
